import localforage from 'localforage';

export interface CacheEntry<T> {
    key: string;
    value: T;
    date: number;
}

const dataStore = localforage.createInstance({ name: 'th_escloud_cache', storeName: 'data' });
const metaStore = localforage.createInstance({ name: 'th_escloud_cache', storeName: 'meta' });

function isQuotaError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        error.message.toLowerCase().includes('quota')
    );
}

async function evictOldest(): Promise<boolean> {
    let oldestKey: string | null = null;
    let oldestDate = Infinity;

    await metaStore.iterate<{ date: number }, void>(({ date }, key) => {
        if (date < oldestDate) {
            oldestDate = date;
            oldestKey = key;
        }
    });

    if (!oldestKey) {
        return false;
    }

    await dataStore.removeItem(oldestKey);
    await metaStore.removeItem(oldestKey);
    return true;
}

export async function setByKey<T extends ArrayBuffer | object>(key: string, value: T): Promise<void> {
    const meta = { date: Date.now() };

    while (true) {
        try {
            // Sequential, NOT Promise.all: a parallel meta write that completes
            // after a quota-failed data write would leave an orphan meta entry,
            // which evictOldest would then re-evict on every retry — infinite loop.
            await dataStore.setItem(key, value);
            await metaStore.setItem(key, meta);
            return;
        } catch (error) {
            if (!isQuotaError(error)) throw error;

            const evicted = await evictOldest();
            if (!evicted) {
                return;
            }
        }
    }
}

/**
 * Bump the LRU date for an existing key without rewriting its value.
 * No-op if the key is not present in the data store.
 */
export async function touch(key: string): Promise<void> {
    await metaStore.setItem(key, { date: Date.now() });
}

export async function getByKey<T extends ArrayBuffer | object>(
    key: string
): Promise<CacheEntry<T> | undefined> {
    const [value, meta] = await Promise.all([
        dataStore.getItem<T>(key),
        metaStore.getItem<{ date: number }>(key),
    ]);
    if (value === null) {
        return undefined;
    }

    return { key, value, date: meta?.date ?? 0 };
}

export async function getOldest<T extends ArrayBuffer | object>(
    index = 0
): Promise<CacheEntry<T> | undefined> {
    const entries: { key: string; date: number }[] = [];
    await metaStore.iterate<{ date: number }, void>(({ date }, key) => {
        entries.push({ key, date });
    });
    const target = entries.sort((a, b) => a.date - b.date)[index];
    if (!target) return undefined;
    return getByKey<T>(target.key);
}

export async function getNewest<T extends ArrayBuffer | object>(
    index = 0
): Promise<CacheEntry<T> | undefined> {
    const entries: { key: string; date: number }[] = [];
    await metaStore.iterate<{ date: number }, void>(({ date }, key) => {
        entries.push({ key, date });
    });
    const target = entries.sort((a, b) => b.date - a.date)[index];
    if (!target) return undefined;
    return getByKey<T>(target.key);
}

export async function getOrSet<T extends ArrayBuffer | object>(key: string, value: T): Promise<T> {
    const existing = await getByKey<T>(key);
    if (existing) {
        return existing.value;
    }
    await setByKey(key, value);
    return value;
}
